/**
 * Light / dark theme: the preference, what it resolves to, and everything outside
 * the CSS that has to follow it — the native status bar, the Telegram header and
 * background, the browser `theme-color`.
 * Contract: docs/design/dark-mode-plan.md
 *
 * The CSS does the real work: the colour roles in index.css are redefined under
 * `html[data-theme="dark"]`. This module only decides that attribute and keeps
 * the platform chrome in step with it.
 *
 * Hallouminati only. GreekBrain always resolves to light — its v2 shell has its
 * own palette — so a shared component rendered there never changes.
 *
 * Auto is the default: a user who never opens Profile › Appearance follows the
 * phone (or Telegram). A stored choice — Light, Dark or Auto, from that row,
 * which every Hallouminati user sees — always wins over the default.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/react';
import type { AppType } from '@/types';
import { getProduct } from '@/product';
import { CHROME } from '@/theme/colors';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

/** The user's choice, written only from Profile › Appearance. */
const PREFERENCE_KEY = 'hal_theme';
/** What a user who never picked gets. */
const DEFAULT_PREFERENCE: ThemePreference = 'system';
/**
 * What the boot script in index.html does before React loads: 'light', 'dark'
 * or 'system'. Absent means the build's default (system in the Hallouminati
 * build, light otherwise). Written here, never by hand, so the product gate
 * and the default live in one place.
 */
const BOOT_KEY = 'hal_theme_boot';

let product: AppType | null = null;
// The boot script may already have gone dark; start from what is on the page.
let resolved: ResolvedTheme =
    typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark'
        ? 'dark'
        : 'light';
const listeners = new Set<() => void>();

function readStoredPreference(): ThemePreference | null {
    try {
        const value = localStorage.getItem(PREFERENCE_KEY);
        return value === 'system' || value === 'light' || value === 'dark' ? value : null;
    } catch {
        return null;
    }
}

export function getThemePreference(): ThemePreference {
    return readStoredPreference() ?? DEFAULT_PREFERENCE;
}

/** What the user picked in Profile › Appearance, or null if they never did (Auto, the default). */
export function getChosenThemePreference(): ThemePreference | null {
    return readStoredPreference();
}

export function getResolvedTheme(): ResolvedTheme {
    return resolved;
}

function telegram() {
    const tg = window.Telegram?.WebApp;
    return tg?.initData ? tg : null;
}

/** Inside Telegram the app follows Telegram's theme, which can differ from the phone's. */
function systemTheme(): ResolvedTheme {
    const tg = telegram();
    if (tg) return tg.colorScheme === 'dark' ? 'dark' : 'light';
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolve(): ResolvedTheme {
    if (product !== 'hallouminati') return 'light';
    const preference = getThemePreference();
    return preference === 'system' ? systemTheme() : preference;
}

function writeBootMode(): void {
    try {
        if (product === 'hallouminati') localStorage.setItem(BOOT_KEY, getThemePreference());
        else localStorage.removeItem(BOOT_KEY);
    } catch {
        // Best effort: without it the next launch paints light first, then switches.
    }
}

/** The `theme-color` a product shows: its brand colour in light, the ground in dark. */
export function themeColorFor(appType: AppType): string {
    return resolved === 'dark' ? CHROME.dark.ground : getProduct(appType).themeColor;
}

function applyThemeColorMeta(): void {
    if (!product) return;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', themeColorFor(product));
}

/** Telegram's header and background. Called by platform.ts at start-up too. */
export function applyTelegramChrome(): void {
    const tg = telegram();
    if (!tg) return;
    try {
        if (resolved === 'dark') {
            tg.setHeaderColor(CHROME.dark.telegramHeader);
            tg.setBackgroundColor(CHROME.dark.telegramHeader);
        } else {
            // Header: eggshell, so iOS renders dark (readable) status-bar text over it.
            tg.setHeaderColor(CHROME.light.telegramHeader);
            // Background: Telegram's own default, which is what light always had.
            tg.setBackgroundColor('bg_color');
        }
    } catch (error) {
        Sentry.captureException(error, { tags: { area: 'theme', step: 'telegram_chrome', tg_version: tg.version } });
    }
}

/** The native status bar text: dark on light, light on dark. Called by platform.ts at start-up too. */
export async function applyStatusBar(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        // Capacitor names the style after the background it suits: Light = dark text.
        await StatusBar.setStyle({ style: resolved === 'dark' ? Style.Dark : Style.Light });
    } catch (error) {
        Sentry.captureException(error, { tags: { area: 'theme', step: 'status_bar' } });
    }
}

function apply(): void {
    const next = resolve();
    const changed = next !== resolved;
    resolved = next;
    document.documentElement.setAttribute('data-theme', next);
    writeBootMode();
    applyThemeColorMeta();
    applyTelegramChrome();
    void applyStatusBar();
    if (changed) listeners.forEach((listener) => listener());
}

export function setThemePreference(preference: ThemePreference): void {
    try {
        localStorage.setItem(PREFERENCE_KEY, preference);
    } catch (error) {
        // The choice still applies for this session; it just won't survive a restart.
        Sentry.captureException(error, { tags: { area: 'theme', step: 'save_preference' } });
    }
    apply();
    listeners.forEach((listener) => listener());
}

/**
 * Starts the theme for a product: resolves it, paints the chrome, and follows the
 * phone (or Telegram) while the preference is 'system'. Returns the cleanup.
 */
function startTheme(appType: AppType): () => void {
    product = appType;
    apply();

    const onSystemChange = () => {
        if (getThemePreference() === 'system') apply();
    };
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    media?.addEventListener('change', onSystemChange);
    const tg = telegram();
    tg?.onEvent('themeChanged', onSystemChange);

    return () => {
        media?.removeEventListener('change', onSystemChange);
        tg?.offEvent('themeChanged', onSystemChange);
    };
}

/** Mounted once, by App.tsx's router, as soon as the product is known. */
export function useThemeController(appType: AppType): void {
    useEffect(() => startTheme(appType), [appType]);
}

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};

/** The stored choice, for Profile › Appearance. Re-renders when it changes. */
export function useThemePreference(): ThemePreference {
    return useSyncExternalStore(subscribe, getThemePreference, () => DEFAULT_PREFERENCE);
}

/** What is on screen right now — for the few JS colours (charts) that must follow it. */
export function useResolvedTheme(): ResolvedTheme {
    return useSyncExternalStore(subscribe, getResolvedTheme, () => 'light');
}
